<template>
  <div class="p-pb-3">
    <Toolbar class="p-mb-4 p-mt-4 p-p-2">
      <template #start>
        <div style="font-size: 1.4em; color: inherit" class="p-p-2">
          {{ $t('billing.subscriptions.subscriptionsLabel') }}
        </div>
      </template>
    </Toolbar>

    <Spinner v-if="isLoading"/>

    <template v-else>
      <div v-if="preview" class="p-grid p-mb-3 subscription-summary">
        <div class="p-col-12 p-sm-6 p-lg-3">
          <div class="p-fieldset subscription-summary__item">
            <div class="subscription-summary__label p-text-secondary">
              {{ $t('billing.subscriptions.currentAccountMonthlyAmountLabel') }}
            </div>
            <div class="subscription-summary__value">
              {{ formatBillingCurrency(isTrial ? 0 : (preview.current?.total || 0), 'EUR') }}
            </div>
          </div>
        </div>

        <div class="p-col-12 p-sm-6 p-lg-3">
          <div class="p-fieldset subscription-summary__item">
            <div class="subscription-summary__label p-text-secondary">
              {{
                requiresPaidActivation
                    ? $t('billing.subscriptions.paidTransitionLabel')
                    : isTrial
                        ? $t('billing.subscriptions.afterTrialPlanLabel')
                        : $t('billing.subscriptions.nextAccountMonthlyAmountLabel')
              }}
            </div>

            <div class="subscription-summary__value">
              <template v-if="requiresPaidActivation">
                {{ trialActivationStatusLabel }}
              </template>

              <template v-else-if="isTrial">
                {{ $t('billing.subscriptions.notSelectedLabel') }}
              </template>

              <template v-else>
                {{ formatBillingCurrency(preview.next?.total || 0, 'EUR') }}
              </template>
            </div>

            <div v-if="trialActivationPaymentPending" class="subscription-summary__hint p-text-secondary">
              {{ $t('billing.subscriptions.trialChangeBlockedHint') }}
            </div>

            <div
                v-if="!isTrial"
                class="subscription-summary__hint p-text-secondary">
              {{ $t('billing.subscriptions.smsChargedSeparatelyLabel') }}
            </div>
          </div>
        </div>


        <div class="p-col-12 p-sm-6 p-lg-3">
          <div class="p-fieldset subscription-summary__item">
            <div class="subscription-summary__label p-text-secondary">
              {{ $t('Employees') }}
            </div>
            <div class="subscription-summary__value">
              {{ preview.usage?.active_employees || 0 }} / {{ preview.current?.included_employees || 0 }}
            </div>
            <div
                v-if="preview.current?.extra_employees"
                class="subscription-summary__hint">
              {{ preview.current.extra_employees }} {{ $t('billing.subscriptions.additionalLabel') }}
              <span class="p-ml-1">· {{ formatBillingCurrency(preview.current.extra_employees_total || 0, 'EUR') }}</span>
            </div>
          </div>
        </div>

        <div class="p-col-12 p-sm-6 p-lg-3">
          <div class="p-fieldset subscription-summary__item">
            <div class="subscription-summary__label p-text-secondary">
              {{ $t('billing.subscriptions.incomingEmailAccountsLabel') }}
            </div>
            <div class="subscription-summary__value">
              {{ preview.usage?.incoming_email_accounts || 0 }} / {{ preview.current?.included_incoming_email_accounts || 0 }}
            </div>
            <div
                v-if="preview.current?.extra_incoming_email_accounts"
                class="subscription-summary__hint">
              {{ preview.current.extra_incoming_email_accounts }} {{ $t('billing.subscriptions.additionalLabel') }}
              <span class="p-ml-1">· {{ formatBillingCurrency(preview.current.extra_incoming_email_accounts_total || 0, 'EUR') }}</span>
            </div>
          </div>
        </div>
      </div>

      <DesktopDataTable
          :dataTableIsLoading="dataTableIsLoading || tenantDataIsLoading"
          :showHeader="false"
          :itemsArrayIsEmpty="itemsArrayIsEmpty"
          :sort="sort"
          :showSearchInput="false"
          :actionButtonsCount="actionButtonsCount"
          :headers="[
            {name: 'location', title: $t('Location'), sortable: false},
            {name: 'company', title: $t('Company'), sortable: false},
            {name: 'plan', title: $t('billing.subscriptions.planLabel'), sortable: false},
            {name: 'price', title: $t('billing.subscriptions.planPriceLabel'), sortable: false},
            {name: 'period', title: $t('Period'), sortable: false},
            {name: 'next_plan', title: $t('billing.subscriptions.scheduledPlanLabel'), isHidden: !hasScheduledPlans, sortable: false},
            {name: 'status', title: $t('Status'), sortable: false},
          ]">
        <template v-slot:body>
          <tr
              v-for="(item, index) of items"
              :key="item.subscription_id ?? index"
              :class="{'updated-item': isItemUpdated(item.subscription_id)}">
            <td>
              <span v-if="item.location_name">{{ item.location_name }}</span>
              <i v-else class="ti-minus"/>
            </td>

            <td>
              <span v-if="item.company_name">{{ item.company_name }}</span>
              <i v-else class="ti-minus"/>
            </td>

            <td>
              <strong>{{ item.current_plan_key }}</strong>
              <div v-if="requiresPaidActivation" class="table-secondary-line">
                {{ $t(trialActivationPaymentPending
                    ? 'billing.subscriptions.paidPlanSelectedLabel'
                    : 'billing.subscriptions.trialAccessLabel') }}
              </div>
            </td>

            <td>
              <div>
                <span>{{ formatBillingCurrency(item.current_price || 0, 'EUR') }}</span>
              </div>
              <div v-if="item.current_discount_active" class="table-secondary-line">
                <span class="subscription-old-price">
                  {{ formatBillingCurrency(item.current_base_price || 0, 'EUR') }}
                </span>

                <span class="p-ml-1">
                  −{{ formatPercent(item.current_discount_percent) }}%
                </span>
              </div>

              <div  v-if="item.current_discount_active && item.current_discount_ends_at"
                    class="table-secondary-line">
                {{ $t('billing.subscriptions.untilDateLabel') }} {{ formatUtcDay(item.current_discount_ends_at - 1) }}
              </div>
            </td>

            <td>
              <span v-if="item.current_period_start && item.current_period_end">
                {{ formatUtcDay(item.current_period_start) }} – {{ formatUtcDay(item.current_period_end - 1) }}
              </span>
              <span v-else-if="item.current_period_start">
                {{ $t('billing.subscriptions.fromDateLabel') }} {{ formatUtcDay(item.current_period_start) }}
              </span>
              <span v-else-if="item.current_period_end">
                {{ $t('billing.subscriptions.untilDateLabel') }} {{ formatUtcDay(item.current_period_end - 1) }}
              </span>
              <i v-else class="ti-minus"/>
            </td>

            <td v-if="hasScheduledPlans">
              <template v-if="item.has_pending_plan">
                <strong>{{ item.pending_plan_key }}</strong>
                <div v-if="item.pending_plan_starts_at" class="table-secondary-line">
                  {{ $t('billing.subscriptions.fromDateLabel') }} {{ formatUtcDay(item.pending_plan_starts_at) }}
                </div>
              </template>
              <i v-else class="ti-minus"/>
            </td>

            <td>
              <div class="p-d-flex p-flex-column p-ai-start">
                <div class="state-badge" :class="subscriptionStatusClass(item)">
                  {{ subscriptionStatusLabel(item) }}
                </div>

                <div v-if="item.cancel_at_period_end" class="table-secondary-line warning-color">
                  {{ $t('billing.subscriptions.cancellationScheduledLabel') }}
                  <template v-if="item.current_period_end">
                    {{ formatUtcDay(item.current_period_end - 1) }}
                  </template>
                </div>
              </div>
            </td>

            <td v-if="actionButtonsCount">
              <div class="actions">
                <Button v-if="!tenantIsInactive && trialActivationPaymentPending"
                        :label="trialInvoiceActionLabel"
                        icon="pi pi-arrow-right"
                        class="p-button-sm p-button-text"
                        style="width: auto; white-space: nowrap"
                        :disabled="dataTableIsLoading || tenantDataIsLoading"
                        @click="goToTrialInvoices"/>
                <Button v-else-if="!tenantIsInactive && item.status_key !== 'suspended'"
                        :label="requiresPaidActivation || item.status_key === 'canceled'
                        ? $t('billing.subscriptions.choosePlanAction')
                        : $t('Change')"
                        :icon="requiresPaidActivation || item.status_key === 'canceled'
                        ? 'pi pi-arrow-right'
                        : 'pi pi-sync'"
                        class="p-button-sm p-button-text"
                        style="width: auto; white-space: nowrap"
                        :disabled="dataTableIsLoading || tenantDataIsLoading"
                        @click="openPlanChangeModal(item)"/>
              </div>
            </td>
          </tr>
        </template>
      </DesktopDataTable>

      <PlanChangeModal v-if="planChangeModalVisible"
                       :visible="planChangeModalVisible"
                       :subscription="selectedSubscription"
                       :required-feature-key="requestedFeatureKey"
                       :required-feature-label="requestedFeatureLabel"
                       :minimum-plan-key="requestedFeatureMinimumPlanKey"
                       :is-trial="requiresPaidActivation"
                       :trial-selection-completes-activation="trialSelectionCompletesActivation"
                       :is-submitting="planChangeIsSubmitting"
                       @close="closePlanChangeModal"
                       @confirm="confirmPlanChange"/>

    </template>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import {planChangeErrorMessage} from '@/services/billingPlanErrors'
import formatMixins from '@/mixins/formatMixins'
import httpMixins from '@/mixins/httpMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import datatableMixins from '@/mixins/datatableMixins'
import settings from '@/settings'
import PlanChangeModal from '@/pages/account/components/PlanChangeModal'
import {sendStorageBroadcast} from '@/mixins/authMixins'

export default {
  components: {
    PlanChangeModal,
  },
  mixins: [
    formatMixins,
    httpMixins,
    showErrorsMixins,
    datatableMixins,
  ],
  props: {
    tenantDataIsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      preview: null,
      planChangeModalVisible: false,
      planChangeIsSubmitting: false,
      selectedSubscription: null,
      requestedFeatureKey: '',
      routePath: '/subscriptions',
      sort: {
        column: 'id',
        direction: '-',
      },
    }
  },
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')

      if (
          this.$route.path === '/login'
          || route.indexOf('account') === -1
          || route.indexOf('subscriptions') === -1
      ) {
        return false
      }

      this.getItems()
    },
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.planChangeModalVisible) {
          this.closePlanChangeModal()
        }
      }
    },
  },
  mounted() {
    this.scrollToTop()

    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) {
      return false
    }

    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true

      const ctrl = this.setController('subscriptionsPreview')
      const requestedTenantId = Number(this.$store.getters.tenantId || 0)

      try {
        const {status, data} = await httpClient({
          url: 'billing/preview',
          method: 'post',
          signal: ctrl?.signal,
        })

        if (status === 200 && data?.success) {
          this.preview = data
          if (requestedTenantId === Number(this.$store.getters.tenantId || 0)) {
            this.$store.commit('billing/SET_TRIAL_ACTIVATION', data)
          }
          this.items = Array.isArray(data.locations) ? data.locations : []
          this.itemsReceivedTimestamp = Date.now()

          this.$nextTick(() => this.openRequestedPlanSelection())
        } else {
          this.preview = null
          this.items = []
          this.showUnknownError()
        }
      } catch (err) {
        this.preview = null
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false

        if (this._abortors?.subscriptionsPreview === ctrl) {
          this._abortors.subscriptionsPreview = null
        }
      }
    },
    openRequestedPlanSelection() {
      if (String(this.$route.query.select_plan || '') !== '1') {
        return
      }

      if (!this.tenantIsInactive && this.trialActivationPaymentPending) {
        return this.goToTrialInvoices(true)
      }

      const locationId = Number(
          this.$route.query.location_id || 0
      )
      const requiredFeatureKey = String(
          this.$route.query.required_feature || ''
      ).trim().toLowerCase()

      const item = this.items.find(subscription => {
        return Number(subscription.location_id) === locationId
      })

      if (item && item.status_key !== 'suspended') {
        this.openPlanChangeModal(item, requiredFeatureKey)
      }

      const query = {
        ...this.$route.query,
      }

      delete query.select_plan
      delete query.location_id
      delete query.required_feature

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query,
      })
    },
    openPlanChangeModal(item, requiredFeatureKey = '') {
      if (this.tenantIsInactive) return

      if (this.trialActivationPaymentPending) {
        return this.goToTrialInvoices()
      }

      this.selectedSubscription = {...item}
      this.requestedFeatureKey = requiredFeatureKey
      this.planChangeModalVisible = true
      this.$store.commit('toggleFirstLayer', true)
    },
    goToTrialInvoices(replace = false) {
      const query = { page: 1, sort: '-id', unpaid: '1' }
      if (this.pendingTrialInvoiceIds.length === 1) {
        query.highlight = this.pendingTrialInvoiceIds[0]
      }
      const target = { name: 'BillingInvoices', query }
      return replace === true
          ? this.$router.replace(target)
          : this.$router.push(target)
    },
    closePlanChangeModal() {
      if (this.planChangeIsSubmitting) return

      this.planChangeModalVisible = false
      this.selectedSubscription = null
      this.requestedFeatureKey = ''
      this.$store.commit('toggleFirstLayer', false)
    },
    async confirmPlanChange(payload) {
      if (this.planChangeIsSubmitting) return

      this.planChangeIsSubmitting = true

      try {
        let endpoint = 'billing/change-plan'

        if (payload.direction === 'activation' && !this.requiresPaidActivation) {
          endpoint = 'billing/reactivate-subscription'
        } else if (payload.direction === 'cancel_pending') {
          endpoint = 'billing/cancel-pending-plan'
        }

        const {status, data} = await httpClient.post(endpoint, {
          subscription_id: payload.subscription_id,
          location_id: payload.location_id,
          to_plan_key: payload.to_plan_key,
        })

        if (status !== 200 || !data?.success) {
          this.$toast.add({severity: 'error', detail: planChangeErrorMessage(data, (key) => this.$t(key)), life: settings.toastLife,
          })
          return
        }

        const direction = data.direction || payload.direction
        const selectedPlanKey = data.subscription?.pending_plan_key
            || data.subscription?.plan_key
            || payload.to_plan_key

        if (data.entitlements) {
          const billingChangePayload = {
            tenant_id: Number(
                this.$store.getters.tenantId || 0
            ),
            location_id: Number(
                payload.location_id || 0
            ),
            entitlements: data.entitlements,
          }

          await this.$store.dispatch('applyPlanChangeEntitlements', billingChangePayload)

          sendStorageBroadcast('BILLING_CHANGED', billingChangePayload)
        }

        const changedSubscriptionId = Number(
            data.subscription?.id
            ?? data.subscription?.subscription_id
            ?? payload.subscription_id
        )

        this.planChangeModalVisible = false
        this.selectedSubscription = null
        this.requestedFeatureKey = ''
        this.$store.commit('toggleFirstLayer', false)

        await this.getItems()

        if (data.action === 'trial_plan_selected') {
          if (changedSubscriptionId > 0) {
            this.updatedItemId = changedSubscriptionId
          }

          this.$toast.add({
            severity: 'success',
            detail: this.$t('billing.subscriptions.trialPlanSelectedMsg', {
              plan: selectedPlanKey,
              count: Number(data.remaining_locations_count || 0),
            }),
            life: settings.toastLife,
          })

          return
        }

        if (direction === 'cancel_pending') {
          if (changedSubscriptionId > 0) {
            this.updatedItemId = changedSubscriptionId
          }

          this.$toast.add({
            severity: 'success',
            detail: this.$t('billing.subscriptions.pendingPlanCanceledMsg'),
            life: settings.toastLife
          })

          return
        }

        if (direction === 'upgrade' || direction === 'activation') {
          const messages = [this.$t('billing.subscriptions.planActivatedMsg', {plan: selectedPlanKey}),]

          if (data.invoice_number) {
            messages.push(this.$t('billing.subscriptions.invoiceIssuedMsg', {invoice: data.invoice_number}))
          }

          this.$toast.add({severity: 'success', detail: messages.join(' '), life: settings.toastLife,})

          if (data.warning) {
            this.$toast.add({severity: 'warn', detail: data.warning, life: settings.toastLife,})
          }

          const invoiceId = Number(data.invoice_id || 0)

          if (invoiceId > 0) {
            await this.$router.push({
              name: 'BillingInvoices',
              query: {
                page: 1,
                sort: '-id',
                unpaid: '1',
                highlight: invoiceId,
              },
            })
          } else if (changedSubscriptionId > 0) {
            this.updatedItemId = changedSubscriptionId
          }

          return
        }

        if (changedSubscriptionId > 0) {
          this.updatedItemId = changedSubscriptionId
        }

        this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife})

      } catch (err) {
        const code = err?.response?.data?.code
        if (['TRIAL_ACTIVATION_ALREADY_PENDING', 'TRIAL_ACTIVATION_PAYMENT_ALREADY_RECORDED'].includes(code)) {
          this.$toast.add({
            severity: 'error',
            detail: planChangeErrorMessage(err.response.data, (key) => this.$t(key)),
            life: settings.toastLife,
          })
        } else {
          this.showError(err)
        }
      } finally {
        this.planChangeIsSubmitting = false
      }
    },
    formatPercent(value) {
      const number = Number(value || 0)

      return Number.isInteger(number)
          ? number
          : number.toFixed(2).replace(/\.00$/, '')
    },
    subscriptionStatusLabel(item) {
      if (this.trialActivationPaymentPending && item?.status_key === 'active') {
        return this.$t('billing.subscriptions.trialPaymentPendingLabel')
      }

      if (this.requiresPaidActivation) {
        return this.$t(
            this.trialIsExpired
                ? 'billing.subscriptions.statuses.trialExpired'
                : 'billing.subscriptions.statuses.trial'
        )
      }

      const statusKey = item?.status_key
      const labels = {
        active: 'billing.subscriptions.statuses.active',
        suspended: 'billing.subscriptions.statuses.suspended',
        canceled: 'billing.subscriptions.statuses.canceled',
      }

      return this.$t(
          labels[statusKey] || 'billing.subscriptions.statuses.unknown'
      )
    },

    subscriptionStatusClass(item) {
      if (this.requiresPaidActivation) {
        return this.trialIsExpired
            ? 'subscription-state--trial-expired'
            // : 'subscription-state--trial'
            : 'purple'
      }

      const statusKey = item?.status_key
      const classes = {
        active: 'green',
        suspended: 'red',
        canceled: 'subscription-state--canceled',
      }

      return classes[statusKey] || 'subscription-state--unknown'
    },
  },
  computed: {
    tenantIsInactive() {
      return this.$store.getters.billingLockReasonKey === 'tenant_locked'
    },
    requestedFeatureLabel() {
      if (!this.requestedFeatureKey) return ''

      return this.$t(
          `billing.features.${this.requestedFeatureKey}.label`
      )
    },
    requestedFeatureMinimumPlanKey() {
      if (!this.requestedFeatureKey) return ''

      return this.$store.getters.requiredPlanFor(
          this.requestedFeatureKey
      )?.minimum_plan_key || ''
    },
    isTrial() {
      return !!this.preview?.is_trial
    },
    requiresPaidActivation() {
      return !!this.preview?.requires_paid_activation
    },
    trialActivationPaymentPending() {
      return this.requiresPaidActivation && this.preview?.trial_activation?.status === 'payment_pending'
    },
    pendingTrialInvoiceIds() {
      return this.preview?.trial_activation?.pending_invoice_ids || []
    },
    trialInvoiceActionLabel() {
      return this.$t(this.pendingTrialInvoiceIds.length === 1
          ? 'billing.subscriptions.openActivationInvoiceAction'
          : 'billing.subscriptions.openActivationInvoicesAction')
    },
    trialActivationStatusLabel() {
      const activation = this.preview?.trial_activation

      if (activation?.status === 'payment_pending') {
        return this.$t('billing.subscriptions.trialPaymentPendingLabel')
      }

      if (Number(activation?.selected_locations_count || 0) > 0) {
        return this.$t('billing.subscriptions.trialSelectedLocationsLabel', {
          selected: activation.selected_locations_count,
          total: activation.total_locations_count,
        })
      }

      return this.$t('billing.subscriptions.paidPlanNotSelectedLabel')
    },
    trialIsExpired() {
      const trialEndsAt = Number(this.preview?.trial_ends_at || 0)

      return trialEndsAt > 0
          && trialEndsAt <= Math.floor(Date.now() / 1000)
    },
    actionButtonsCount() {
      return this.tenantIsInactive ? 0 : 1
    },
    hasScheduledPlans() {
      return this.items.some(item => item.has_pending_plan)
    },
    trialLocationsWithoutPlanCount() {
      if (!this.requiresPaidActivation) return 0

      return this.items.filter(item => !item.has_pending_plan).length
    },
    trialSelectionCompletesActivation() {
      if (!this.requiresPaidActivation || !this.selectedSubscription) {
        return false
      }

      if (this.selectedSubscription.has_pending_plan) {
        return this.trialLocationsWithoutPlanCount === 0
      }

      return this.trialLocationsWithoutPlanCount === 1
    },
  },
}
</script>

<style scoped lang="scss">
td {
  height: 52px;
}

.subscription-summary__item {
  height: 100%;
  padding: 14px 16px;
}

.subscription-summary__label {
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.subscription-summary__value {
  font-size: 1.35rem;
  font-weight: 600;
}

.subscription-summary__hint,
.table-secondary-line {
  font-size: 0.82rem;
  margin-top: 3px;
}

.subscription-old-price {
  text-decoration: line-through;
}

//.subscription-state--trial {
//  background: #bbdefb;
//  color: #0d47a1;
//}

.subscription-state--trial-expired {
  background: #ffecb3;
  color: #8a5a00;
}

//.subscription-state--active {
//  background: #c8e6c9;
//  color: #256029;
//}

//.subscription-state--suspended {
//  background: #ffcdd2;
//  color: #c63737;
//}

.subscription-state--canceled,
.subscription-state--unknown {
  background: #e0e0e0;
  color: #495057;
}

</style>